import './EntryErrorModalContent.css';

import { useEffect, useRef, useState } from 'react';

import { useTranslate } from '../../../hooks/context/useTranslate';
import { EntryErrorDefinition } from '../../../types/types';
import SeptimLoadingSpinner from '../../Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import ModalBody from '../ControlActionModalContent/components/ModalBody/ModalBody';
import BasicEntryContent from './components/BasicEntryContent/BasicEntryContent';
import CardEntryContent from './components/CardEntryContent/CardEntryContent';
import DateTimeEntryContent from './components/DateTimeEntryContent/DateTimeEntryContent';
import ListEntryContent from './components/ListEntryContent/ListEntryContent';

export interface EntryErrorModalContentProps {
  text: string;
  entry: EntryErrorDefinition;
  submitEntryValue: (value: string) => void | Promise<void>;
  cancelModalAction: () => void | Promise<void>;
}

const EntryErrorModalContent = ({
  text,
  entry,
  submitEntryValue,
  cancelModalAction,
}: EntryErrorModalContentProps) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Chained entry errors reuse this modal instance with a new action instead
  // of remounting — the key remounts the content so the entered value resets.
  const [contentKey, setContentKey] = useState<number>(0);

  const prevActionRef = useRef(submitEntryValue);

  const { t } = useTranslate();

  if (prevActionRef.current !== submitEntryValue) {
    prevActionRef.current = submitEntryValue;
    setIsSubmitting(false);
    setContentKey((key) => key + 1);
  }

  const handleAction = async (action: () => void | Promise<void>) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await action();
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    switch (entry.type) {
      case 'string':
      case 'int':
      case 'float':
        return (
          <BasicEntryContent
            key={contentKey}
            valueType={entry.type}
            defaultValue={entry.default}
            isSubmitting={isSubmitting}
            handleAction={handleAction}
            submitEntryValue={submitEntryValue}
            cancelModalAction={cancelModalAction}
          />
        );
      case 'date':
      case 'time':
      case 'datetime':
        return (
          <DateTimeEntryContent
            key={contentKey}
            valueType={entry.type}
            defaultValue={entry.default}
            isSubmitting={isSubmitting}
            handleAction={handleAction}
            submitEntryValue={submitEntryValue}
            cancelModalAction={cancelModalAction}
          />
        );
      case 'list':
        return (
          <ListEntryContent
            key={contentKey}
            list={entry.list}
            isSubmitting={isSubmitting}
            handleAction={handleAction}
            submitEntryValue={submitEntryValue}
            cancelModalAction={cancelModalAction}
          />
        );
      case 'card':
        return (
          <CardEntryContent
            key={contentKey}
            allowManual={entry.allowManual}
            isSubmitting={isSubmitting}
            handleAction={handleAction}
            submitEntryValue={submitEntryValue}
            cancelModalAction={cancelModalAction}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ModalBody
      header={t('entryError.title')}
      text={text}
      buttons={
        isSubmitting ? (
          <SeptimLoadingSpinner className="entry-error-modal-spinner" />
        ) : (
          <div className="entry-error-modal-content">{renderContent()}</div>
        )
      }
    />
  );
};

export default EntryErrorModalContent;
